import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Hls from "hls.js";
import {
  Box,
  Container,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Link,
  IconButton,
  LinearProgress,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import Button from "@mui/material/Button";
import axiosInstance from "../api/axiosConfig";
import {
  downloadEndpoint,
  downloadErrorMessage,
} from "../utils/downloadUtils";
import VideoList from "./VideoList";

interface VideoDetails {
  title: string;
  processingComplete: boolean;
  artist?: string;
  filesize?: number;
  resolution?: string;
  isHLS?: boolean;
  sourceUrl?: string | null;
  site?: string | null;
  processingError?: string | null;
  processingErrorCode?: string | null;
}

interface RandomVideo {
  vid: string;
  title: string;
}

const useVideoPlayer = (
  videoRef: React.RefObject<HTMLVideoElement>,
  autoPlayNext: boolean,
  onVideoEnd: () => void,
  videoDetails: VideoDetails | null,
  vid: string | undefined,
  useHls: boolean
) => {
  const hlsRef = useRef<Hls | null>(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const isInitialized = useRef(false);
  const currentVid = useRef<string | undefined>();
  
  // Use refs to track these values without triggering the main setup effect
  const autoPlayNextRef = useRef(autoPlayNext);
  const onVideoEndRef = useRef(onVideoEnd);

  useEffect(() => {
    autoPlayNextRef.current = autoPlayNext;
  }, [autoPlayNext]);

  useEffect(() => {
    onVideoEndRef.current = onVideoEnd;
  }, [onVideoEnd]);

  useEffect(() => {
    // helper: setup media session metadata & action handlers
    const setupMediaSession = () => {
      const v = videoRef.current;
      if (!v || !('mediaSession' in navigator)) return;
      try {
        const ms = (navigator as any).mediaSession;
        ms.metadata = new (window as any).MediaMetadata({
          title: videoDetails?.title || '',
          artist: videoDetails?.artist || '',
          artwork: [
            { src: `${backendUrl}/api/thumbnail/${vid}`, sizes: '512x512', type: 'image/jpeg' }
          ]
        });

        ms.setActionHandler('play', () => { v.play(); });
        ms.setActionHandler('pause', () => { v.pause(); });
        ms.setActionHandler('seekbackward', (details: any) => { v.currentTime = Math.max(0, v.currentTime - (details?.seekOffset || 10)); });
        ms.setActionHandler('seekforward', (details: any) => { v.currentTime = Math.min(v.duration, v.currentTime + (details?.seekOffset || 10)); });
        ms.setActionHandler('previoustrack', () => { /* no-op - could emit an event for skipping back */ });
        ms.setActionHandler('nexttrack', () => { onVideoEnd(); });
      } catch (e) {
        // Ignore errors (e.g., unsupported MediaMetadata)
      }
    };
    const video = videoRef.current;
    if (!video || !vid || !videoDetails) return;

    console.log("Setting up video player for vid:", vid);

    // Store current playback state before cleanup
    const currentTime = video.currentTime;
    const wasPlaying = !video.paused;

    // Ensure any previous playback is fully stopped and unloaded to avoid overlapping audio
    try {
      if (!video.paused) video.pause();
      // remove src and force unload of any previous media resource
      if (video.src) {
        video.removeAttribute('src');
      }
      // some browsers support srcObject for media streams
      // @ts-ignore
      if (video.srcObject) {
        // @ts-ignore
        video.srcObject = null;
      }
      // force the element to unload
      // @ts-ignore - HTMLMediaElement.load exists
      video.load();
    } catch (e) {
      // ignore
    }

    // Clean up previous HLS instance
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (e) { /* ignore */ }
      hlsRef.current = null;
    }

    // Check if video uses HLS and user has enabled HLS
    const shouldUseHls = Boolean(videoDetails?.isHLS && useHls);
    
    if (shouldUseHls) {
      const videoSrc = `${backendUrl}/api/hls/${vid}/playlist.m3u8`;
      
      if (Hls.isSupported()) {
        // Use HLS.js for browsers that don't natively support HLS
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          maxBufferLength: 90,
          maxMaxBufferLength: 600,
          maxBufferSize: 100 * 1000 * 1000,
          maxBufferHole: 0.5,
          highBufferWatchdogPeriod: 3,
          nudgeOffset: 0.1,
          nudgeMaxRetry: 3,
          maxFragLookUpTolerance: 0.25,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: Infinity,
          liveDurationInfinity: false,
          stretchShortVideoTrack: false,
          forceKeyFrameOnDiscontinuity: true,
          abrEwmaFastLive: 3,
          abrEwmaSlowLive: 9,
          abrEwmaFastVoD: 3,
          abrEwmaSlowVoD: 9,
          abrEwmaDefaultEstimate: 5e5,
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,
          abrMaxWithRealBitrate: false,
          maxStarvationDelay: 4,
          maxLoadingDelay: 4,
          minAutoBitrate: 0,
          emeEnabled: false,
          widevineLicenseUrl: undefined,
          requestMediaKeySystemAccessFunc: undefined,
        });

        hlsRef.current = hls;

        // Load the HLS source
        hls.loadSource(videoSrc);
        hls.attachMedia(video);

        // Handle HLS events
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log("HLS manifest loaded");
          // Restore playback state after loading new video
          if (currentTime > 0) {
            video.currentTime = currentTime;
          }
          video.play().catch(error => {
            console.warn("Autoplay prevented:", error);
          });
          // Setup media session and handlers
          setupMediaSession();
        });

  hls.on(Hls.Events.ERROR, (event: any, data: any) => {
          if (data.fatal) {
            console.error("Fatal HLS error:", data);
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log("Fatal network error encountered, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log("Fatal media error encountered, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                console.error("Unrecoverable error");
                hls.destroy();
                break;
            }
          }
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (event: any, data: any) => {
          console.log(`HLS level switched to ${data.level}`);
        });

      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari) - Better for AirPlay
        video.src = videoSrc;
        video.onloadedmetadata = () => {
          // Restore playback state
          if (currentTime > 0) {
            video.currentTime = currentTime;
          }
          video.play().catch(error => {
            console.warn("Autoplay prevented:", error);
          });
          // setup media session
          setupMediaSession();
        };
      } else {
        console.error("HLS is not supported in this browser");
      }
    } else {
      // Use regular video streaming - This is crucial for AirPlay compatibility
      const videoSrc = `${backendUrl}/api/v/${vid}`;
      video.src = videoSrc;
      
      // Set up proper metadata for AirPlay
      video.onloadedmetadata = () => {
        console.log("Video metadata loaded, duration:", video.duration);
        // Restore playback state
        if (currentTime > 0) {
          video.currentTime = currentTime;
        }
        if (autoPlayNext || wasPlaying) {
          video.play().catch(error => {
            console.warn("Autoplay prevented:", error);
          });
        }
        // setup media session
        setupMediaSession();
      };
    }

    // Enhanced video element configuration for better AirPlay support
    video.tabIndex = -1;
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    
    // Set video poster/title for better AirPlay experience (commented out to avoid hover tooltip)
    
    // Add webkit-playsinline for iOS compatibility
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('playsinline', 'true');

    // Keyboard controls
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!video || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      switch (e.key) {
        case 'p':
        case 'P':
        case ' ':
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            video.requestFullscreen();
          }
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          video.muted = !video.muted;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - 5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          video.currentTime = Math.min(video.duration, video.currentTime + 5);
          break;
        case 'ArrowUp':
          e.preventDefault();
          video.volume = Math.min(1, video.volume + 0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          video.volume = Math.max(0, video.volume - 0.1);
          break;
      }
    };

    const handleVideoKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
        return false;
      }
    };
    
    const handleError = (e: Event) => {
      console.error("Video error:", e);
      const target = e.target as HTMLVideoElement;
      if (target.error) {
        console.error("Video error code:", target.error.code);
        console.error("Video error message:", target.error.message);
      }
    };

    const handleEnded = () => {
      // Always destroy HLS instance first
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
 
      // Ensure the current video is fully stopped and its media pipeline is torn down
      try {
        video.pause();
        // remove src and reset the media element to prevent overlapping playback when navigating
        video.removeAttribute('src');
        // force the element to unload
        // @ts-ignore - HTMLMediaElement.load exists
        video.load();
      } catch (e) {
        // ignore
      }
 
      if (autoPlayNextRef.current) {
        onVideoEndRef.current();
      } else if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    };

    const handleWaiting = () => {
      console.log("Video waiting for data...");
    };

    const handleCanPlay = () => {
      console.log("Video can start playing");
    };

    const handleLoadStart = () => {
      console.log("Video load started");
    };

    // AirPlay specific event handlers
    const handleWebkitPlaybackTargetAvailabilityChanged = (e: any) => {
      console.log("AirPlay target availability changed:", e.availability);
    };

    // Media Session position/timeupdate handlers
    const handleTimeUpdate = () => {
      const v = videoRef.current;
      if (!v) return;
      try {
        if ('mediaSession' in navigator) {
          const ms = (navigator as any).mediaSession;
          if (ms.setPositionState) {
            ms.setPositionState({
              duration: Number.isFinite(v.duration) ? v.duration : NaN,
              position: v.currentTime,
              playbackRate: v.playbackRate
            });
          }
        }
      } catch (e) {
        // ignore
      }
    };

    const handlePlayEvent = () => {
      try { if ('mediaSession' in navigator) (navigator as any).mediaSession.playbackState = 'playing'; } catch (e) {}
    };

    const handlePauseEvent = () => {
      try { if ('mediaSession' in navigator) (navigator as any).mediaSession.playbackState = 'paused'; } catch (e) {}
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyPress);
    video.addEventListener('keydown', handleVideoKeyDown, true);
    video.addEventListener("error", handleError);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("loadstart", handleLoadStart);
  video.addEventListener('timeupdate', handleTimeUpdate);
  video.addEventListener('play', handlePlayEvent);
  video.addEventListener('pause', handlePauseEvent);
    
    // AirPlay event listeners (WebKit specific)
    video.addEventListener('webkitplaybacktargetavailabilitychanged', handleWebkitPlaybackTargetAvailabilityChanged);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      video.removeEventListener('keydown', handleVideoKeyDown, true);
      video.removeEventListener("error", handleError);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener('webkitplaybacktargetavailabilitychanged', handleWebkitPlaybackTargetAvailabilityChanged);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlayEvent);
      video.removeEventListener('pause', handlePauseEvent);
      // Additional cleanup: make sure playback is stopped and HLS is destroyed when effect re-runs
      try {
        if (!video.paused) video.pause();
        if (video.src) video.removeAttribute('src');
        // @ts-ignore
        if (video.srcObject) video.srcObject = null;
        // @ts-ignore
        video.load();
      } catch (e) {
        // ignore
      }
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (e) { /* ignore */ }
        hlsRef.current = null;
      }
    };
  }, [vid, videoDetails, backendUrl, useHls, videoRef]); // eslint-disable-line react-hooks/exhaustive-deps -- autoPlayNext and onVideoEnd are accessed via refs

  // Handle autoplay changes separately without reinitializing the video
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isInitialized.current) return;

    if (autoPlayNext && video.paused && video.readyState >= 2) {
      video.play().catch(error => {
        console.warn("Autoplay prevented:", error);
      });
    }
  }, [autoPlayNext, videoRef]);

  // Reset initialization when video ID changes
  useEffect(() => {
    if (currentVid.current !== vid) {
      isInitialized.current = false;
    }
  }, [vid]);

  // Cleanup HLS on unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);
};

interface VideoPlayerProps {
  searchResults?: any[];
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ searchResults }) => {
  const { vid } = useParams<{ vid: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoDetails, setVideoDetails] = useState<VideoDetails | null>(null);
  const [autoPlayNext, setAutoPlayNext] = useState<boolean>(() => localStorage.getItem("autoPlayNext") === "true");
  const [nextVideos, setNextVideos] = useState<RandomVideo[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(0);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<boolean>(false);
  const [useHls, setUseHls] = useState<boolean>(() => localStorage.getItem("useHls") === "true");
  const [listMode] = useState<'default' | 'random'>(() =>
    (localStorage.getItem("videoListMode") as 'default' | 'random' | null) || 'default'
  );
  const pollIntervalRef = useRef<NodeJS.Timeout>();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchRandomVideos = useCallback(async () => {
    try {
      if(nextVideos.length > 0) return;
      let response;
      if (listMode === 'random') {
        // Use seeded random API for consistent random list
        const seed = Math.random().toString(36).slice(2, 10);
        response = await axiosInstance.get(
          `${backendUrl}/api/videos/random-seeded?seed=${seed}&page=1&limit=12`
        );
      } else {
        response = await axiosInstance.get(`${backendUrl}/api/videos/random?currentVid=${vid}&limit=1`);
      }
      setNextVideos(response.data.videos);
    } catch (error) {
      console.error("Error fetching random videos:", error);
    }
  }, [backendUrl, nextVideos.length, vid, listMode]);

  const handleVideoEnd = useCallback(() => {
    if (autoPlayNext && nextVideos.length > 0) {
      const filteredVideos = nextVideos.filter(video => video.vid !== vid);
      
      let nextVideo;
      let remainingVideos;
      
      if (filteredVideos.length > 0) {
        nextVideo = filteredVideos[0];
        remainingVideos = filteredVideos.slice(1);
      } else {
        nextVideo = nextVideos[1];
        remainingVideos = nextVideos.slice(2);
      }
      
      setNextVideos(remainingVideos);
      
      if (nextVideo) {
        navigate(`/video/${nextVideo.vid}`, { replace: true });
      }
    }
  }, [autoPlayNext, nextVideos, vid, navigate]);

  useEffect(() => {
    setNextVideos([]);
  }, [vid]);

  useEffect(() => {
    setNextVideos([]);
  }, [listMode]);

  // Use the updated hook with videoDetails and vid
  useVideoPlayer(videoRef, autoPlayNext, handleVideoEnd, videoDetails, vid, useHls);

  useEffect(() => {
    setIsProcessing(true);
    setProgress(0);
    setDownloadError(null);
    axiosInstance
      .get(`${backendUrl}/api/stats/${vid}`)
      .then((response) => {
        const details: VideoDetails = response.data;
        setVideoDetails(details);
        setIsProcessing(!details.processingComplete);
        if (!details.processingComplete && details.processingError) {
          setDownloadError(details.processingError);
          setIsProcessing(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching video details:", error);
        setIsProcessing(false);
      });
  }, [vid, backendUrl]);

  useEffect(() => {
    document.title = videoDetails?.title ? `${videoDetails.title} - MediaPlayer` : 'MediaPlayer';
    return () => { document.title = 'MediaPlayer'; };
  }, [videoDetails?.title]);

  useEffect(() => {
    if (autoPlayNext && nextVideos.length < 2) {
      fetchRandomVideos();
    }
  }, [nextVideos.length, autoPlayNext, fetchRandomVideos]);

  useEffect(() => {
    if (!vid || !isProcessing) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }

    const pollProgress = async () => {
      try {
        const response = await axiosInstance.get(`${backendUrl}/api/progress/${vid}`);
        setProgress(response.data.progress);

        // the queue gives up with a reason (cookies, private post, no video, ...)
        if (response.data.failed) {
          clearInterval(pollIntervalRef.current);
          setDownloadError(response.data.error || "The download failed.");
          setIsProcessing(false);
          return;
        }

        if (!response.data.isProcessing) {
          clearInterval(pollIntervalRef.current);
          const statsResponse = await axiosInstance.get(`${backendUrl}/api/stats/${vid}`);
          setVideoDetails(statsResponse.data);
          setIsProcessing(false);
        }
      } catch (error) {
        console.error("Error polling progress:", error);
      }
    };

    pollProgress();
    pollIntervalRef.current = setInterval(pollProgress, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [vid, isProcessing, backendUrl]);

  const handleAutoPlayNextToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setAutoPlayNext(newValue);
    localStorage.setItem("autoPlayNext", String(newValue));
    if (newValue) {
      fetchRandomVideos();
    }
  };

  /** Re-submit the original source URL (X, Facebook, Threads, ...). */
  const handleRetry = async () => {
    const sourceUrl = videoDetails?.sourceUrl;
    if (!sourceUrl) {
      navigate("/");
      return;
    }
    setRetrying(true);
    try {
      const response = await axiosInstance.post(downloadEndpoint(sourceUrl, backendUrl), { url: sourceUrl });
      const newVid = response?.data?.videoId;
      setDownloadError(null);
      setProgress(0);
      setIsProcessing(true);
      // a retry creates a fresh video row, so move to it
      if (newVid && newVid !== vid) navigate(`/video/${newVid}`, { replace: true });
    } catch (error: any) {
      setDownloadError(downloadErrorMessage(error, sourceUrl));
      setIsProcessing(false);
    } finally {
      setRetrying(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Container maxWidth={false} sx={{ mt: 0, mb: 4 }}>
      {isProcessing ? (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Processing video: {videoDetails?.title || "Loading..."}
          </Typography>
          <Box sx={{ width: "80%", my: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Typography variant="body1">{progress}% complete</Typography>
        </Paper>
      ) : downloadError ? (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            textAlign: "center",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="error">
            Could not download {videoDetails?.title || "this video"}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 640, whiteSpace: "pre-wrap" }}>
            {downloadError}
          </Typography>
          {videoDetails?.processingErrorCode && (
            <Typography variant="caption" color="text.secondary">
              error code: {videoDetails.processingErrorCode}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
            <Button variant="contained" onClick={handleRetry} disabled={retrying} startIcon={retrying ? <CircularProgress size={16} /> : null}>
              Try again
            </Button>
            <Button variant="text" onClick={() => navigate("/")}>
              Back to library
            </Button>
          </Box>
          {videoDetails?.sourceUrl && (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 700, wordBreak: "break-all" }}>
              source: {videoDetails.sourceUrl}
            </Typography>
          )}
        </Paper>
      ) : (
        <video
          ref={videoRef}
          style={{
            backgroundColor: "#000000ff",
            width: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            outline: "none",
            border: "none",
            ...(isMobile && {
              position: 'sticky',
              top: 48,
              zIndex: 1000,
              maxHeight: "55vh"
            })
          }}
          autoPlay={true}
          controls
          playsInline
          preload="metadata"
          crossOrigin="anonymous"
          muted={false}
          tabIndex={-1}
          // Enhanced AirPlay support attributes
          x-webkit-airplay="allow"
          webkit-playsinline="true"
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        />
      )}
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mt: isProcessing ? 2 : { xs: 0, sm: 2 },
          mb: 3,
          borderRadius: { xs: 0, sm: 4 },
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          border: { xs: 'none', sm: '1px solid rgba(255,255,255,0.1)' },
          borderTop: { xs: '1px solid rgba(255,255,255,0.1)', sm: '1px solid rgba(255,255,255,0.1)' }
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {videoDetails?.title || (isProcessing ? "" : "Loading title...")}
                {videoDetails?.isHLS ? (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ 
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: useHls ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.text.secondary, 0.1),
                      color: useHls ? 'success.main' : 'text.secondary', 
                      cursor: 'pointer', 
                      userSelect: 'none',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => {
                      const newVal = !useHls;
                      setUseHls(newVal);
                      localStorage.setItem("useHls", String(newVal));
                    }}
                    aria-disabled={!videoDetails?.isHLS}
                    title={useHls ? 'Playing via HLS. Click to disable.' : 'Click to play via HLS'}
                  >
                    HLS {useHls ? 'ON' : 'OFF'}
                  </Typography>
                ) : (
                  <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
                    (NO HLS)
                  </Typography>
                )}
            </Typography>
            {!isProcessing && videoDetails && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {videoDetails?.filesize ? `${(videoDetails.filesize / 1048576).toFixed(2)} MB` : ''}&nbsp;•&nbsp;{videoDetails?.resolution || 'Unknown resolution'}
              </Typography>
            )}
          </Box>
          {!isProcessing && videoDetails && (
            <IconButton 
              size="medium" 
              onClick={() => navigate(`/music/${vid}`)}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
              title="Play as Audio"
            >
              <MusicNoteIcon />
            </IconButton>
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <FormControlLabel
            control={<Switch checked={autoPlayNext} onChange={handleAutoPlayNextToggle} color="primary" size="small" />}
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Autoplay Next</Typography>}
            sx={{ m: 0 }}
          />
        </Box>
        {autoPlayNext && nextVideos.length > 0 && (
          <Box mt={1.5} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.paper, 0.6), border: '1px solid rgba(255,255,255,0.05)' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
              Up Next
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link
                component="a"
                href={`/video/${nextVideos[0].vid}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/video/${nextVideos[0].vid}`);
                }}
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                    cursor: "pointer",
                  },
                }}
              >
                {nextVideos[0].title}
              </Link>
            </Typography>
          </Box>
        )}
      </Paper>
      <VideoList searchResults={searchResults} mode={listMode} />
    </Container>
  );
};

export default VideoPlayer;